import type {
	ExtensionParams,
	Parameters as ExtensionParameters,
} from '@atlaskit/editor-common/extensions';
import type { ContextId, ForgeUIExtensionType } from '@atlassian/forge-ui-types/extension';

export type MacroCoreParameters = ExtensionParameters & {
	autoConvertLink?: string;
	config?: Record<string, unknown>;
	embeddedMacroContext?: Partial<{
		accountId: string;
		cloudId: string;
		contextIds: string[];
		extensionData: Record<string, unknown>;
	}>;
	extensionId?: string;
	extensionTitle?: string;
	guestParams?: Record<string, unknown>;
	layout?: string;
	macroParams?: Record<string, { value?: unknown }>;
};

type ExtensionData = {
	[key: string]: unknown;
	content?: { id?: string; subtype?: string | null; type?: string };
	references?: unknown[];
	space?: { id?: string; key?: string };
	type?: string;
};

const FORGE_MACRO_MODULE_TYPE = 'xen:macro';
const STATIC_MODULE_KEY_SEGMENT = '/static/';

export const getConnectModuleKeyFromForgeExtensionId = (
	extensionId?: string,
): string | undefined => {
	if (!extensionId) {
		return;
	}

	const staticModuleKeyIndex = extensionId.indexOf(STATIC_MODULE_KEY_SEGMENT);
	if (staticModuleKeyIndex === -1) {
		return;
	}

	return extensionId.slice(staticModuleKeyIndex + STATIC_MODULE_KEY_SEGMENT.length);
};

export const getMigratedForgeMacroExtension = (
	forgeMacroExtensions: ReadonlyArray<ForgeUIExtensionType> | null | undefined,
	extensionKey?: string,
): ForgeUIExtensionType | undefined => {
	if (!extensionKey || !forgeMacroExtensions) {
		return;
	}

	return forgeMacroExtensions.find(
		(extension) =>
			extension.type === FORGE_MACRO_MODULE_TYPE &&
			Boolean(extension.migrationKey) &&
			getConnectModuleKeyFromForgeExtensionId(extension.id) === extensionKey,
	);
};

export const getExtensionLayout = (extension?: ForgeUIExtensionType): string | undefined => {
	if (extension?.properties?.layout === 'inline') {
		return 'inlineExtension';
	}
	return undefined;
};

export const createForgeParametersForConnectMacro = ({
	accountId,
	cloudId,
	extension,
	extensionData,
	productContextIds,
	props,
}: {
	accountId?: string | null;
	cloudId?: string;
	extension: ForgeUIExtensionType;
	extensionData?: ExtensionData;
	productContextIds?: ContextId[];
	props: ExtensionParams<MacroCoreParameters>;
}): MacroCoreParameters | undefined => {
	if (!cloudId || !productContextIds || !accountId) {
		return;
	}

	const parameters: MacroCoreParameters = { ...(props.parameters ?? {}) };
	const guestParams: Record<string, unknown> = { ...(parameters.guestParams ?? {}) };

	Object.entries(parameters.macroParams ?? {}).forEach(([key, macroParam]) => {
		if (typeof guestParams[key] === 'undefined') {
			guestParams[key] = macroParam?.value;
		}
	});

	return {
		...parameters,
		embeddedMacroContext: {
			...parameters.embeddedMacroContext,
			accountId: parameters.embeddedMacroContext?.accountId ?? accountId,
			cloudId: parameters.embeddedMacroContext?.cloudId ?? cloudId,
			contextIds: parameters.embeddedMacroContext?.contextIds ?? productContextIds,
			extensionData: parameters.embeddedMacroContext?.extensionData ?? {
				type: 'macro',
				...(extensionData?.content?.id
					? {
							content: {
								id: extensionData.content.id,
								type: extensionData.content.type ?? 'page',
							},
						}
					: {}),
			},
		},
		extensionId: parameters.extensionId ?? extension.id,
		extensionTitle:
			parameters.extensionTitle ??
			(typeof extension.properties?.title === 'string' ? extension.properties.title : undefined),
		guestParams,
		layout: parameters.layout ?? getExtensionLayout(extension),
	};
};
